import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import FamilyService from "../services/FamilyService";
import { useEnsureSessionReady } from "./useEnsureSessionReady";
import type {
  SendInviteRequest,
  FamilyGroup,
  FamilyMember,
  PendingInvitesResponse,
  MemberHealthData,
  PendingMealApproval,
  MealApprovalResponse,
  CreateGroupResponse,
  GroupListResponse,
  GroupListItem,
} from "../types/family";
import { message } from "antd";

// UUID validation helper
const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const isValidUUID = (id: string): boolean => uuidRegex.test(id);

// Query Keys
export const familyKeys = {
  all: ["family"] as const,
  groups: () => [...familyKeys.all, "groups"] as const,
  pendingInvites: () => [...familyKeys.all, "pendingInvites"] as const,
  inviteDetails: (token: string) =>
    [...familyKeys.all, "inviteDetails", token] as const,
  mealApprovalDetails: (token: string) =>
    [...familyKeys.all, "mealApprovalDetails", token] as const,
};

/**
 * Hook to get all groups
 */
export const useGroups = (options?: { enabled?: boolean }) => {
  const { canUseAuthenticatedApis } = useEnsureSessionReady();
  const extraEnabled = options?.enabled ?? true;

  return useQuery<GroupListResponse>({
    queryKey: familyKeys.groups(),
    queryFn: () => FamilyService.getInstance().getGroups(),
    enabled: canUseAuthenticatedApis && extraEnabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
  });
};

/**
 * Hook to get pending invites
 */
export const usePendingInvites = () => {
  return useQuery<PendingInvitesResponse>({
    queryKey: familyKeys.pendingInvites(),
    queryFn: () => FamilyService.getInstance().getPendingInvites(),
    staleTime: 60 * 1000, // 1 minute
    gcTime: 5 * 60 * 1000,
  });
};

/**
 * Hook to get invite details by token
 */
export const useInviteDetails = (
  token: string,
  options?: { enabled?: boolean }
) => {
  return useQuery({
    queryKey: familyKeys.inviteDetails(token),
    queryFn: () => FamilyService.getInstance().getInviteDetails(token),
    enabled: !!token && (options?.enabled ?? true),
    staleTime: 0, // Always fetch fresh
    retry: false, // Don't retry on error (invalid token)
  });
};

/**
 * Mutation hook to create group
 */
export const useCreateGroup = () => {
  const queryClient = useQueryClient();

  return useMutation<CreateGroupResponse, any, string>({
    mutationFn: (name: string) =>
      FamilyService.getInstance().createGroup(name),
    onSuccess: (data) => {
      if (data.group_id && isValidUUID(data.group_id)) {
        localStorage.setItem("family_group_id", data.group_id);
      }
      queryClient.invalidateQueries({ queryKey: familyKeys.groups() });
      queryClient.invalidateQueries({ queryKey: ["homePageData"] });
      message.success(data.message || "Family group created successfully!");
    },
    onError: (error: any) => {
      console.error("Error creating group:", error);
      const errorMessage = error?.response?.data?.error?.message || error?.response?.data?.message;
      message.error(errorMessage || "Failed to create group");
    },
  });
};

/**
 * Mutation hook to send invite
 */
export const useSendInvite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (request: SendInviteRequest) =>
      FamilyService.getInstance().sendInvite(request),
    onSuccess: () => {
      // Invalidate home data to show updated family list
      queryClient.invalidateQueries({ queryKey: ["homePageData"] });
      message.success("Invite sent successfully!");
    },
    onError: (error: any) => {
      console.error("Error sending invite:", error);
      
      // Handle specific error codes
      const errorCode = error?.response?.data?.error?.code;
      const errorMessage = error?.response?.data?.error?.message;
      
      if (errorCode === "no_smtp_config") {
        // SMTP configuration missing - suggest using link method
        message.error({
          content: "Email service not configured. Please use 'Link' method to generate an invite link instead.",
          duration: 5,
        });
      } else if (errorMessage) {
        message.error(errorMessage);
      } else if (error?.response?.data?.message) {
        message.error(error.response.data.message);
      } else {
        message.error("Failed to send invite. Please try again or use Link method.");
      }
    },
  });
};

/**
 * Mutation hook to accept invite
 */
export const useAcceptInvite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (token: string) =>
      FamilyService.getInstance().acceptInvite(token),
    onSuccess: (data) => {
      // Store group_id and membership_id if needed
      if (data.group_id && isValidUUID(data.group_id)) {
        localStorage.setItem("family_group_id", data.group_id);
      }
      if (data.membership_id) {
        localStorage.setItem("family_membership_id", data.membership_id);
      }
      
      // Invalidate home data to show the new member
      queryClient.invalidateQueries({ queryKey: ["homePageData"] });
      
      message.success("You've joined the family circle!");
    },
    onError: (error: any) => {
      console.error("Error accepting invite:", error);
      
      const errorCode = error?.response?.data?.error?.code;
      const errorMessage = error?.response?.data?.error?.message;
      const invitedEmail = error?.response?.data?.error?.invited_email;
      const currentEmail = error?.response?.data?.error?.current_email;
      
      // Handle specific error codes
      if (errorCode === "email_mismatch") {
        message.error(
          `Wrong Account! This invite was sent to ${invitedEmail}, but you're logged in as ${currentEmail}. Please logout and sign in with the correct email.`,
          8
        );
      } else if (errorCode === "cannot_accept_own_group") {
        message.warning({
          content: "You cannot accept your own invitation! Please share this link with a family member or friend.",
          duration: 6,
        });
      } else if (errorCode === "already_accepted") {
        message.warning("This invitation has already been accepted.");
      } else if (errorCode === "invite_expired" || errorCode === "invalid_token") {
        message.error({
          content: "This invitation link is invalid or has expired. Please ask for a new invitation.",
          duration: 6,
        });
      } else if (errorCode === "already_member") {
        message.info("You are already a member of this family group.");
      } else if (errorMessage) {
        message.error(errorMessage);
      } else {
        message.error("Failed to accept invite. Please try again.");
      }
    },
  });
};

/**
 * Mutation hook to reject invite
 */
export const useRejectInvite = () => {
  return useMutation({
    mutationFn: ({ token, reason }: { token: string; reason?: string }) =>
      FamilyService.getInstance().rejectInvite(token, reason),
    onSuccess: () => {
      message.success("Invite declined");
    },
    onError: (error: any) => {
      console.error("Error rejecting invite:", error);
      message.error(
        error?.response?.data?.message || "Failed to reject invite."
      );
    },
  });
};

/**
 * Mutation hook to cancel invite
 */
export const useCancelInvite = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (inviteId: string) =>
      FamilyService.getInstance().cancelInvite(inviteId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: familyKeys.pendingInvites() });
      message.success("Invite cancelled");
    },
    onError: (error) => {
      console.error("Error canceling invite:", error);
      message.error("Failed to cancel invite");
    },
  });
};

/**
 * Mutation hook to send nudge
 */
export const useSendNudge = () => {
  return useMutation({
    mutationFn: ({ memberId, nudgeMessage }: { memberId: string; nudgeMessage: string }) =>
      FamilyService.getInstance().sendNudge(memberId, nudgeMessage),
    onSuccess: () => {
      message.success("Nudge sent!");
    },
    onError: (error) => {
      console.error("Error sending nudge:", error);
      message.error("Failed to send nudge");
    },
  });
};

/**
 * Hook to get meal approval details by token
 */
export const useMealApprovalDetails = (
  token: string,
  options?: { enabled?: boolean }
) => {
  return useQuery<PendingMealApproval>({
    queryKey: familyKeys.mealApprovalDetails(token),
    queryFn: () => FamilyService.getInstance().getMealApprovalDetails(token),
    enabled: !!token && (options?.enabled ?? true),
    staleTime: 0,
    retry: false,
  });
};



/**
 * Mutation hook to approve meal
 */
export const useApproveMeal = () => {
  const queryClient = useQueryClient();

  return useMutation<MealApprovalResponse, any, string>({
    mutationFn: (token: string) =>
      FamilyService.getInstance().approveMeal(token),
    onSuccess: () => {
      // Invalidate home data to show the new meal
      queryClient.invalidateQueries({ queryKey: ["homePageData"] });
      // message.success("Meal approved and added to your log!");
    },
    onError: (error: any) => {
      console.error("Error approving meal:", error);
      const errorMessage = error?.response?.data?.error?.message || error?.response?.data?.message;
      message.error(errorMessage || "Failed to approve meal");
    },
  });
};

/**
 * Mutation hook to deny meal
 */
export const useDenyMeal = () => {
  const queryClient = useQueryClient();
  return useMutation<MealApprovalResponse, any, { token: string; reason?: string }>({
    mutationFn: ({ token, reason }) =>
      FamilyService.getInstance().denyMeal(token, reason),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homePageData"] });
      message.success("Meal log declined");
    },
    onError: (error: any) => {
      console.error("Error denying meal:", error);
      const errorMessage = error?.response?.data?.error?.message || error?.response?.data?.message;
      message.error(errorMessage || "Failed to deny meal");
    },
  });
};

/**
 * Mutation hook to approve current user's group membership (when is_approve = true)
 */
export const useApproveMembership = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; message: string }, any, string>({
    mutationFn: (groupId: string) =>
      FamilyService.getInstance().approveMembership(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homePageData"] });
      queryClient.invalidateQueries({ queryKey: familyKeys.groups() });
      message.success("You've joined the group!");
    },
    onError: (error: any) => {
      console.error("Error approving membership:", error);
      const errorMessage = error?.response?.data?.error?.message || error?.response?.data?.message;
      message.error(errorMessage || "Failed to approve membership");
    },
  });
};



/**
 * Mutation hook to reject current user's group membership (when is_approve = true)
 */
export const useRejectMembership = () => {
  const queryClient = useQueryClient();
  return useMutation<{ success: boolean; message: string }, any, string>({
    mutationFn: (groupId: string) =>
      FamilyService.getInstance().rejectMembership(groupId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["homePageData"] });
      queryClient.invalidateQueries({ queryKey: familyKeys.groups() });
      message.success("Group invitation declined");
    },
    onError: (error: any) => {
      console.error("Error rejecting membership:", error);
      const errorMessage = error?.response?.data?.error?.message || error?.response?.data?.message;
      message.error(errorMessage || "Failed to reject membership");
    },
  });
};
